import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Search, ArrowRight, Layers } from 'lucide-react';
import { EventTemplate } from '../../core/types.ts';

interface TemplateModalProps {
  isOpen: boolean;
  templates: EventTemplate[];
  onClose: () => void;
  onSelectTemplate: (template: EventTemplate) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  Culture: 'var(--cat-culture)',
  Drinking: 'var(--cat-drinking)',
  Sports: 'var(--cat-sports)',
  Social: 'var(--cat-social)',
  Academic: 'var(--cat-academic)',
  Trip: 'var(--cat-trip)',
  Art: 'var(--cat-art)',
  Other: 'var(--cat-other)',
};

export const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  templates,
  onClose,
  onSelectTemplate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredTemplates = templates.filter(tmpl => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      tmpl.title.toLowerCase().includes(q) ||
      (tmpl.category && tmpl.category.toLowerCase().includes(q)) ||
      (tmpl.description && tmpl.description.toLowerCase().includes(q))
    );
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{
          width: '92vw',
          maxWidth: '1200px',
          height: '85vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Fixed Header */}
        <div className="modal-header" style={{ padding: '1.25rem 1.75rem', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-sm)',
                background: 'color-mix(in srgb, var(--cat-social) 15%, transparent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--cat-social)',
                border: '1px solid color-mix(in srgb, var(--cat-social) 30%, transparent)',
              }}
            >
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="modal-title" style={{ fontSize: '1.25rem', lineHeight: 1.2 }}>
                Event Templates
              </h2>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                Select a template to quickly pre-populate event details
              </p>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </div>

        {/* Search Bar Container */}
        <div
          className="template-search-container"
          style={{
            padding: '1rem 1.75rem',
            flexShrink: 0,
          }}
        >
          <div className="search-box" style={{ width: '100%', maxWidth: '100%' }}>
            <Search size={18} strokeWidth={2} className="search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search templates by title, category, or keyword..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
              style={{
                fontSize: '1rem',
                paddingTop: '0.85rem',
                paddingBottom: '0.85rem',
              }}
            />
            {searchQuery && (
              <button
                className="icon-btn search-clear-btn"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
                title="Clear search"
              >
                <X size={14} strokeWidth={2} />
              </button>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '0.65rem',
              fontSize: '0.8rem',
              color: 'var(--text-muted)',
            }}
          >
            <span>
              Showing <strong style={{ color: 'var(--text-primary)' }}>{filteredTemplates.length}</strong> of{' '}
              {templates.length} templates
            </span>
            {searchQuery && (
              <span>
                Filter active: &quot;<span style={{ color: 'var(--text-secondary)' }}>{searchQuery}</span>&quot;
              </span>
            )}
          </div>
        </div>

        {/* Scrollable Compact List Container */}
        <div
          className="modal-body"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '1.25rem 1.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.6rem',
          }}
        >
          {filteredTemplates.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4rem 2rem',
                color: 'var(--text-muted)',
                textAlign: 'center',
                gap: '0.75rem',
              }}
            >
              <Layers size={36} style={{ opacity: 0.4 }} />
              <p style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                No templates matching &quot;{searchQuery}&quot;
              </p>
              <p style={{ fontSize: '0.85rem', maxWidth: '360px' }}>
                Try searching with a different keyword or clear your search to view all available templates.
              </p>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
                style={{ marginTop: '0.5rem' }}
              >
                Clear Search
              </button>
            </div>
          ) : (
            filteredTemplates.map(tmpl => {
              const catColor = tmpl.category ? CATEGORY_COLORS[tmpl.category] || 'var(--cat-other)' : 'var(--cat-other)';

              return (
                <div
                  key={tmpl.templateId}
                  className="template-compact-row"
                  onClick={() => onSelectTemplate(tmpl)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.85rem 1.15rem',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    gap: '1rem',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: tmpl.description ? '0.25rem' : 0 }}>
                      {tmpl.category && (
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            padding: '0.15rem 0.55rem',
                            borderRadius: 'var(--radius-sm)',
                            background: `color-mix(in srgb, ${catColor} 15%, transparent)`,
                            color: catColor,
                            border: `1px solid color-mix(in srgb, ${catColor} 30%, transparent)`,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {tmpl.category}
                        </span>
                      )}
                      <h3
                        style={{
                          fontSize: '0.98rem',
                          fontWeight: 700,
                          color: 'var(--text-primary)',
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tmpl.title}
                      </h3>
                    </div>

                    {tmpl.description && (
                      <p
                        style={{
                          fontSize: '0.825rem',
                          color: 'var(--text-muted)',
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tmpl.description}
                      </p>
                    )}
                  </div>

                  <button
                    className="btn btn-secondary btn-sm template-use-btn"
                    style={{
                      gap: '0.4rem',
                      flexShrink: 0,
                      padding: '0.4rem 0.85rem',
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      onSelectTemplate(tmpl);
                    }}
                  >
                    <span>Use Template</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Fixed Footer */}
        <div
          className="modal-footer"
          style={{
            padding: '1rem 1.75rem',
            flexShrink: 0,
            justifyContent: 'flex-end',
          }}
        >
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

